import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, CalendarDays, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";
import { applyPageMeta } from "../lib/seo";
import { PHONE_TEL, PHONE_DISPLAY, SITE_URL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <main data-testid="blog-page">
      <PageHero
        eyebrow="PocketBima Blog"
        title="Insurance,"
        highlight="Explained Simply."
        subtitle="Short, honest guides on motor, health, life, travel and business insurance — written for everyday Indians, not insurance experts."
      />
      <section className="bg-white py-14" data-testid="blog-intro-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
              Why We Write These Guides
            </h2>
            <p className="mt-5 text-base leading-relaxed text-charcoal/85">
              Insurance in India is often sold with jargon — waiting periods, IDV, NCB, co-pay —
              and families end up buying policies they don't fully understand. Every guide on this
              blog is written by the Pocket Bima Insurance Service team in Greater Noida West to
              explain one insurance idea at a time, in plain everyday language, so you can make
              confident decisions before you spend a rupee.
            </p>
            <p className="mt-4 text-base leading-relaxed text-charcoal/85">
              Topics we cover: motor insurance for cars, bikes, taxis and commercial vehicles;
              health insurance and how waiting periods really work; term and life insurance
              planning for every income level; travel insurance for domestic holidays and
              international trips; and business insurance for shops, offices and goods transport
              across Greater Noida West, Noida, Orai and Delhi NCR. New guides are published
              regularly — and if there's a topic you'd like us to explain, just call or WhatsApp us.
            </p>
            <p className="mt-4 text-base leading-relaxed text-charcoal/85">
              Every article ends the same way: with honesty. We never promise the lowest premium,
              the "best" policy or guaranteed claims — because those depend on the insurer and your
              details. What we do promise is simple explanations, transparent pros and cons, and a
              real person to talk to when you're ready. That is what Chhoti Pocket, Badi Suraksha
              means to us.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ice py-20" data-testid="blog-list-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
          {posts === null && (
            <div className="flex justify-center py-20" data-testid="blog-loading">
              <Loader2 className="h-8 w-8 animate-spin text-mint" />
            </div>
          )}
          {posts?.length === 0 && (
            <p className="py-20 text-center font-semibold text-charcoal/60" data-testid="blog-empty">
              New guides are on the way — check back soon!
            </p>
          )}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts?.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                  data-testid={`blog-card-${p.slug}`}
                >
                  {p.image && (
                    <div className="overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-7">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">
                      <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(p.created_at)}
                    </p>
                    <h3 className="mt-3 font-display text-xl font-bold leading-snug text-navy transition-colors group-hover:text-mint-600">
                      {p.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-charcoal/75">{p.excerpt}</p>
                    <span className="mt-5 flex items-center gap-1.5 text-sm font-bold text-mint-600">
                      Read Guide <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    setPost(undefined);
    axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    applyPageMeta(
      post.meta_title || `${post.title} | Pocket Bima Insurance Service`,
      post.meta_description || post.excerpt,
      `/blog/${post.slug}`
    );
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      image: post.image ? [SITE_URL + post.image] : undefined,
      datePublished: post.created_at,
      author: { "@type": "Organization", name: "PocketBima" },
      publisher: { "@type": "Organization", name: "PocketBima" },
      mainEntityOfPage: SITE_URL + `/blog/${post.slug}`,
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [post, slug]);

  return (
    <main data-testid="blog-post-page">
      <section className="bg-navy py-16 lg:py-20" data-testid="blog-post-hero">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-mint transition-colors hover:text-white" data-testid="blog-back-link">
            <ArrowLeft className="h-4 w-4" /> All Guides
          </Link>
          {post === undefined ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-mint" /></div>
          ) : post === null ? (
            <h1 className="mt-6 font-display text-3xl font-extrabold text-white" data-testid="blog-post-missing">
              This guide doesn't exist (or is still a draft).
            </h1>
          ) : (
            <>
              <p className="mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-mint">
                <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(post.created_at)}
              </p>
              <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="blog-post-title">
                {post.title}
              </h1>
            </>
          )}
        </div>
      </section>

      {post && (
        <section className="bg-ice py-16" data-testid="blog-post-body">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            {post.image && (
              <Reveal>
                <div className="overflow-hidden rounded-[2rem] border border-navy/10 shadow-lift">
                  <img src={post.image} alt={post.title} className="aspect-[16/9] w-full object-cover" data-testid="blog-post-image" />
                </div>
              </Reveal>
            )}
            <Reveal delay={0.1}>
              <div className="mt-10 space-y-6" data-testid="blog-post-content">
                {post.content.split(/\n\n+/).map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-charcoal/90 md:text-lg">
                    {para}
                  </p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-14 rounded-[2rem] bg-navy p-8 text-center sm:p-10" data-testid="blog-post-cta">
                <h2 className="font-display text-2xl font-extrabold text-white">
                  Have a question about your own policy?
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm text-white/70">
                  Talk to the PocketBima team — simple guidance, no charge.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-4">
                  <a href={PHONE_TEL} className="rounded-full bg-mint px-7 py-3.5 text-sm font-bold text-white shadow-mint transition-all hover:-translate-y-0.5 hover:bg-mint-600" data-testid="blog-post-call">
                    Call {PHONE_DISPLAY}
                  </a>
                  <Link to="/contact" className="rounded-full border-2 border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10" data-testid="blog-post-quote">
                    Get a Free Quote
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </main>
  );
}
